import { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Cpu, FileCode, ArrowRight,
  Bug, Sparkles, CheckCircle, Send,
  GitPullRequest, ExternalLink, Pencil, Link2, AlertCircle,
  Lightbulb, ShieldAlert, ArrowLeft,
} from 'lucide-react';
import { Spinner, DocCardSection, type DocCardItem } from '@systemdox/ui';
import { standardsApi, type StandardsAnalysis } from '@/services/standardsApi';
import { guardrailsApi, type PublishToCiResult } from '@/services/guardrailsApi';
import type { CiFailure } from '@/services/fitnessApi';
import { techStackApi } from '@/services/techStackApi';
import { workflowApi } from '@/services/workflowApi';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import { usePublishJob } from '@/contexts/PublishJobContext';
import { useTenantSlug, useConsoleUrl } from '@/hooks/useConsoleUrl';
import { launchUrl } from '@/utils/subdomain';
import { EditableGuardrailEntry } from '@/components/guardrails/EditableGuardrailEntry';
import { standardsSections } from '@/pages/standards/sections';
import { GapAnalysisSection } from '@/pages/insights/GapAnalysisSection';
import { useCapabilities } from '@/hooks/useCapabilities';
import { useTenant } from '@/contexts/TenantContext';
import { hasScope } from '@/lib/grants';
import { VALIDATE_SECTIONS } from '@/components/docs/validateSections';
import { ChangesEntryCard } from '@/components/changes/ChangesEntryCard';

const STORAGE_KEY = 'learn-report-issue';

interface SubmitResult {
  guardrails?: { saved: boolean; pr?: PublishToCiResult; error?: string };
  techStack?: { saved: boolean; count: number };
  claudeMd?: { published: boolean };
}

function loadPersistedState() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as {
      analysis: StandardsAnalysis;
      issueText: string;
      submitResult: SubmitResult;
    };
  } catch {
    return null;
  }
}

interface InsightsHandoff {
  failures?: CiFailure[];
  issueText?: string;
}

export default function InsightsPage() {
  const { selectedRepo } = useWorkspace();
  const persisted = loadPersistedState();

  // Arrives from a red cell on the fitness matrix (#800): the real check output
  // and the commit it failed on, not a blank textarea to retype it into.
  const handoff = (useLocation().state ?? {}) as InsightsHandoff;
  const [failures] = useState<CiFailure[]>(handoff.failures ?? []);

  const [issueText, setIssueText] = useState(handoff.issueText ?? persisted?.issueText ?? '');
  const [analyzing, setAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState<StandardsAnalysis | null>(
    handoff.issueText ? null : (persisted?.analysis ?? null)
  );
  const [submitting, setSubmitting] = useState(false);
  const [submittingSection, setSubmittingSection] = useState<string | null>(null);
  const [submitResult, setSubmitResult] = useState<SubmitResult>(persisted?.submitResult ?? {});
  const [editing, setEditing] = useState<Record<string, boolean>>({});
  const [ciConfigured, setCiConfigured] = useState(false);
  const { publishing: publishingCi, result: ciResult, error: ciError, start: startPublishToCi } = usePublishJob();

  useEffect(() => {
    guardrailsApi.getCiSettings().then((s) => setCiConfigured(s.configured)).catch(() => {});
  }, []);

  useEffect(() => {
    if (ciResult) {
      setSubmitResult((prev) => {
        const updated = { ...prev, guardrails: { saved: true, pr: ciResult } };
        persist(analysis, issueText, updated);
        return updated;
      });
    } else if (ciError && !publishingCi) {
      setSubmitResult((prev) => {
        if (!prev.guardrails) return prev;
        const updated = { ...prev, guardrails: { saved: true, error: ciError } };
        persist(analysis, issueText, updated);
        return updated;
      });
    }
  }, [ciResult, ciError, publishingCi]); // eslint-disable-line react-hooks/exhaustive-deps

  const persist = useCallback((a: StandardsAnalysis | null, text: string, sr: SubmitResult) => {
    if (a) {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ analysis: a, issueText: text, submitResult: sr }));
    } else {
      sessionStorage.removeItem(STORAGE_KEY);
    }
  }, []);

  async function handleAnalyze() {
    if (!issueText.trim()) return;
    setAnalyzing(true);
    setAnalysis(null);
    setSubmitResult({});
    persist(null, '', {});
    try {
      const result = await standardsApi.analyzeIssue(issueText, failures);
      setAnalysis(result);
      persist(result, issueText, {});
    } finally {
      setAnalyzing(false);
    }
  }

  async function submitGuardrails(): Promise<SubmitResult['guardrails']> {
    if (!analysis?.recommendations.guardrails?.entries.length) return undefined;
    await guardrailsApi.saveEntries(analysis.recommendations.guardrails.entries);
    if (ciConfigured) {
      startPublishToCi();
    }
    return { saved: true };
  }

  async function submitTechStack() {
    if (!analysis?.recommendations.techStack?.entries.length) return null;
    for (const entry of analysis.recommendations.techStack.entries) {
      await techStackApi.add(entry);
    }
    return { saved: true, count: analysis.recommendations.techStack.entries.length };
  }

  async function submitClaudeMd() {
    if (!analysis?.recommendations.claudeMd || !selectedRepo) return null;
    const generated = await workflowApi.generateClaudeMd(selectedRepo, selectedRepo.split('/')[1]);
    const result = await workflowApi.publishClaudeMd(selectedRepo, generated.content);
    return result;
  }

  async function handleSubmitAll() {
    if (!analysis) return;
    setSubmitting(true);
    setSubmitResult({});
    try {
      const results: SubmitResult = {};

      if (analysis.recommendations.guardrails?.entries.length) {
        try {
          results.guardrails = await submitGuardrails();
        } catch { /* individual section errors don't block others */ }
      }
      if (analysis.recommendations.techStack?.entries.length) {
        try {
          results.techStack = (await submitTechStack()) ?? undefined;
        } catch { /* individual section errors don't block others */ }
      }
      if (analysis.recommendations.claudeMd?.suggestions.length) {
        try {
          results.claudeMd = (await submitClaudeMd()) ?? undefined;
        } catch { /* individual section errors don't block others */ }
      }

      setSubmitResult(results);
      persist(analysis, issueText, results);
    } finally {
      setSubmitting(false);
    }
  }

  async function handleSubmitSection(key: 'guardrails' | 'techStack' | 'claudeMd') {
    setSubmittingSection(key);
    try {
      const results = { ...submitResult };
      if (key === 'guardrails') {
        results.guardrails = await submitGuardrails();
      } else if (key === 'techStack') {
        const r = await submitTechStack();
        if (r) results.techStack = r;
      } else if (key === 'claudeMd') {
        const r = await submitClaudeMd();
        if (r) results.claudeMd = r;
      }
      setSubmitResult(results);
      persist(analysis, issueText, results);
    } finally {
      setSubmittingSection(null);
    }
  }

  function updateGuardrailEntry(index: number, field: string, value: string) {
    if (!analysis?.recommendations.guardrails) return;
    const updated = { ...analysis };
    const entries = [...updated.recommendations.guardrails!.entries];
    entries[index] = { ...entries[index], [field]: value };
    updated.recommendations.guardrails = { ...updated.recommendations.guardrails!, entries };
    setAnalysis(updated);
    persist(updated, issueText, submitResult);
  }

  function updateTechStackEntry(index: number, field: string, value: string) {
    if (!analysis?.recommendations.techStack) return;
    const updated = { ...analysis };
    const entries = [...updated.recommendations.techStack!.entries];
    entries[index] = { ...entries[index], [field]: value };
    updated.recommendations.techStack = { ...updated.recommendations.techStack!, entries };
    setAnalysis(updated);
    persist(updated, issueText, submitResult);
  }

  function updateClaudeMdSuggestion(index: number, value: string) {
    if (!analysis?.recommendations.claudeMd) return;
    const updated = { ...analysis };
    const suggestions = [...updated.recommendations.claudeMd!.suggestions];
    suggestions[index] = value;
    updated.recommendations.claudeMd = { ...updated.recommendations.claudeMd!, suggestions };
    setAnalysis(updated);
    persist(updated, issueText, submitResult);
  }

  function clearAnalysis() {
    setAnalysis(null);
    setSubmitResult({});
    setIssueText('');
    persist(null, '', {});
  }

  const recKeys = analysis
    ? (Object.keys(analysis.recommendations) as (keyof StandardsAnalysis['recommendations'])[]).filter(
        (k) => analysis.recommendations[k],
      )
    : [];

  const hasAnySubmitted = Object.keys(submitResult).length > 0;
  const isAnySubmitting = submitting || submittingSection !== null;

  // The dashboard lives on the launch home (the tenant's bare host) — a
  // cross-host hop, and one that needs the slug to have a host at all.
  const slug = useTenantSlug();

  // The Validate quality gates live here now, not on the Docs hub
  // (#1137): they are the loop's live signal, not documentation. The pages run
  // on the Build console, so the cards cross-host there; gated on build access
  // and hidden per disabled capability, exactly as the Docs hub gated them.
  const consoleUrl = useConsoleUrl();
  const { isDisabled } = useCapabilities();
  const { tenant } = useTenant();
  const canBuild = hasScope(tenant?.grants, 'build:read');
  const checkItems: DocCardItem[] = VALIDATE_SECTIONS
    .filter((s) => s.key !== 'fitness' || !isDisabled('fitness'))
    .map((s) => {
      const href = consoleUrl ? consoleUrl(s.path) : null;
      return {
        key: s.key,
        icon: s.icon,
        iconBg: s.cardIconBg,
        iconColor: s.cardIconColor,
        title: s.label,
        description: s.description,
        href: href ?? undefined,
        disabled: !href,
        cardTitle: href ? `Open ${s.label} on the Build console` : 'Open the Build console to view this',
      };
    });

  return (
    <div className="p-8">
      {slug && (
        <div className="mb-2">
          <a
            href={launchUrl(slug)}
            className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Dashboard
          </a>
        </div>
      )}
      <div className="mb-8">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
            <Lightbulb className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">Insights</h1>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Your feedback loop — watch the quality gates, then turn failures and coverage gaps into the standards your AI follows.
            </p>
          </div>
        </div>
      </div>

      {/* Stage four of a change, Learn: the entry to the Changes pages from
          the feedback loop (ADR-201 Amendment 2 point 10). Example counts
          behind the "Functionality available soon" banner. */}
      <ChangesEntryCard variant="learn" className="mb-8" />

      {/* Quality gates — the Validate checks (Architecture Fitness, Test
          Reports, CI/CD, API Contracts), moved here from the Docs hub (#1137).
          Titled "Quality gates", not "Checks", to avoid colliding with the
          guardrails "Checks" standard a recommendation can surface below. Live
          status is the loop's signal: a failing gate is where a fix begins, and
          the fitness matrix hands its red cells straight into "Report an issue"
          below. The pages run on the Build console, so these cross-host there;
          gated on build access, like the rest of Validate. */}
      {canBuild && (
        <div className="mb-8">
          <DocCardSection
            heading="Quality gates"
            hint="Live status · runs on the Build console"
            items={checkItems}
          />
        </div>
      )}

      {/* Issue Input Panel */}
      <div className="mb-8 rounded-lg border border-purple-200 dark:border-purple-800 bg-purple-50/50 dark:bg-purple-900/10 p-6">
        <div className="flex items-center gap-2 mb-3">
          <Bug className="w-5 h-5 text-purple-600" />
          <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
            Report an Issue
          </h3>
        </div>
        <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
          Describe a problem, mistake, or improvement area. AI will analyze it and recommend which standards need updating.
        </p>
        {failures.length > 0 && (
          <div className="mb-4 rounded-md border border-purple-300 dark:border-purple-700 bg-white dark:bg-slate-900 px-4 py-3">
            <div className="flex items-center gap-2 text-sm font-medium text-purple-800 dark:text-purple-200">
              <ShieldAlert className="w-4 h-4" />
              {failures.length} CI failure{failures.length > 1 ? 's' : ''} attached
            </div>
            <ul className="mt-2 space-y-1">
              {failures.map((f) => (
                <li key={`${f.repo}-${f.checkName}`} className="text-xs text-slate-600 dark:text-slate-400 font-mono truncate">
                  {f.checkName}
                  {f.repo ? ` — ${f.repo}` : ''}
                  {f.commit ? ` @ ${f.commit.slice(0, 7)}` : ''}
                </li>
              ))}
            </ul>
            <p className="mt-2 text-xs text-slate-500">
              The full check output travels with this analysis, so the recommendation is grounded in what CI actually reported.
            </p>
          </div>
        )}
        <textarea
          value={issueText}
          onChange={(e) => setIssueText(e.target.value)}
          placeholder={`Examples:\n• "The AI keeps importing @sentry/react directly instead of using shared-observability"\n• "We added Tailwind CSS v4 to the project but the tech stack doesn't reflect it"\n• "Lambda handlers use claims.get('sub') instead of resolve_principal()"`}
          rows={6}
          className="w-full rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-4 py-3 text-sm text-slate-900 dark:text-slate-100 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-purple-500 resize-y"
        />
        <div className="flex items-center justify-between mt-4">
          {analysis && (
            <button
              onClick={clearAnalysis}
              className="text-sm text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
            >
              Clear
            </button>
          )}
          {!analysis && <div />}
          <button
            onClick={handleAnalyze}
            disabled={analyzing || !issueText.trim()}
            className="flex items-center gap-2 px-4 py-2 rounded-md bg-purple-600 text-white text-sm font-medium hover:bg-purple-700 disabled:opacity-50"
          >
            {analyzing ? <Spinner size="sm" tone="current" /> : <Sparkles className="w-4 h-4" />}
            {analyzing ? 'Analyzing...' : 'Analyze'}
          </button>
        </div>

        {/* Analysis Results */}
        {analysis && (
          <div className="mt-6 space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                  AI Recommendation
                </h3>
                <p className="text-xs text-slate-500 mt-0.5">{analysis.summary}</p>
              </div>
              {!hasAnySubmitted && recKeys.length > 0 && (
                <button
                  onClick={handleSubmitAll}
                  disabled={isAnySubmitting}
                  className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-emerald-600 text-white text-xs font-medium hover:bg-emerald-700 disabled:opacity-50"
                >
                  {submitting ? <Spinner size="sm" tone="current" /> : <Send className="w-3.5 h-3.5" />}
                  {submitting ? 'Submitting...' : 'Submit All'}
                </button>
              )}
              {hasAnySubmitted && (
                <span className="flex items-center gap-1.5 text-xs text-emerald-600 font-medium">
                  <CheckCircle className="w-3.5 h-3.5" /> Submitted
                </span>
              )}
            </div>

            {/* PR Results Banner */}
            {submitResult.guardrails?.pr && (
              <div className="px-4 py-3 rounded-md bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800">
                <div className="flex items-center gap-2 text-sm font-medium text-emerald-700 dark:text-emerald-300">
                  <GitPullRequest className="w-4 h-4" />
                  Guardrails PR created: {submitResult.guardrails.pr.scriptsCreated} script(s) published
                  {submitResult.guardrails.pr.scriptsRemoved > 0 && `, ${submitResult.guardrails.pr.scriptsRemoved} removed`}
                </div>
                <a
                  href={submitResult.guardrails.pr.prUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 mt-1.5 text-sm text-emerald-600 dark:text-emerald-400 hover:underline"
                >
                  <ExternalLink className="w-3.5 h-3.5" />
                  View Pull Request #{submitResult.guardrails.pr.prNumber}
                </a>
              </div>
            )}
            {submitResult.guardrails?.saved && submitResult.guardrails.error && (
              <div className="px-4 py-3 rounded-md bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
                <div className="flex items-center gap-2 text-sm font-medium text-amber-700 dark:text-amber-300">
                  <AlertCircle className="w-4 h-4" />
                  Guardrails saved, but PR creation failed: {submitResult.guardrails.error}
                </div>
                <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">
                  You can publish to CI later from the Guardrails page.
                </p>
              </div>
            )}
            {submitResult.guardrails?.saved && publishingCi && (
              <div className="px-4 py-3 rounded-md bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
                <div className="flex items-center gap-2 text-sm font-medium text-blue-700 dark:text-blue-300">
                  <Spinner size="sm" tone="current" />
                  Guardrails saved. Creating PR...
                </div>
              </div>
            )}
            {submitResult.guardrails?.saved && !submitResult.guardrails.pr && !submitResult.guardrails.error && !publishingCi && !ciConfigured && (
              <div className="px-4 py-3 rounded-md bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800">
                <div className="flex items-center gap-2 text-sm font-medium text-emerald-700 dark:text-emerald-300">
                  <CheckCircle className="w-4 h-4" />
                  Checks saved. Configure CI integration to publish as a PR.
                </div>
              </div>
            )}

            {submitResult.techStack && (
              <div className="px-4 py-3 rounded-md bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
                <div className="flex items-center gap-2 text-sm font-medium text-blue-700 dark:text-blue-300">
                  <CheckCircle className="w-4 h-4" />
                  Tech Stack: {submitResult.techStack.count} {submitResult.techStack.count === 1 ? 'entry' : 'entries'} saved
                </div>
              </div>
            )}

            {submitResult.claudeMd && (
              <div className="px-4 py-3 rounded-md bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800">
                <div className="flex items-center gap-2 text-sm font-medium text-purple-700 dark:text-purple-300">
                  <CheckCircle className="w-4 h-4" />
                  CLAUDE.md published to repository
                </div>
              </div>
            )}

            {/* Duplicate Matches */}
            {analysis.duplicates && analysis.duplicates.length > 0 && (
              <div className="rounded-md border border-amber-200 dark:border-amber-800 bg-amber-50/50 dark:bg-amber-900/10 p-4">
                <h4 className="text-sm font-semibold text-amber-800 dark:text-amber-200 mb-2 flex items-center gap-2">
                  <Link2 className="w-4 h-4" />
                  Already covered by existing standards
                </h4>
                <div className="space-y-2">
                  {analysis.duplicates.map((dup) => (
                    <div key={dup.id} className="flex items-start gap-2 text-xs">
                      {dup.type === 'guardrail' ? (
                        <ShieldAlert className="w-3 h-3 text-rose-500 shrink-0 mt-0.5" />
                      ) : (
                        <Cpu className="w-3 h-3 text-blue-500 shrink-0 mt-0.5" />
                      )}
                      <div>
                        <Link
                          to={dup.type === 'guardrail' ? '/docs/guardrails' : '/docs/tech-stack'}
                          className="font-medium text-purple-600 hover:text-purple-700 dark:text-purple-400 hover:underline"
                        >
                          {dup.title}
                        </Link>
                        <span className="text-slate-500 dark:text-slate-400 ml-1">— {dup.explanation}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {recKeys.map((key) => {
              const section = standardsSections.find((s) => s.key === key);
              if (!section) return null;
              const rec = analysis.recommendations[key]!;
              const Icon = section.icon;
              const isEditing = editing[key];
              const isSectionSubmitted = key in submitResult;
              const isSectionSubmitting = submittingSection === key;

              return (
                <div
                  key={key}
                  className="rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-4"
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <div className={`w-7 h-7 rounded-md ${section.iconBg} flex items-center justify-center`}>
                        <Icon className={`w-4 h-4 ${section.iconColor}`} />
                      </div>
                      <h4 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                        {section.title}
                      </h4>
                    </div>
                    <div className="flex items-center gap-2">
                      {!isSectionSubmitted && !isEditing && !isAnySubmitting && (
                        <button
                          onClick={() => setEditing({ ...editing, [key]: true })}
                          className="text-xs text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 flex items-center gap-1"
                        >
                          <Pencil className="w-3 h-3" /> Refine
                        </button>
                      )}
                      {isEditing && !isAnySubmitting && (
                        <button
                          onClick={() => setEditing({ ...editing, [key]: false })}
                          className="text-xs text-purple-600 hover:text-purple-700 dark:text-purple-400 font-medium"
                        >
                          Done editing
                        </button>
                      )}
                      {!isSectionSubmitted && !hasAnySubmitted && (
                        <button
                          onClick={() => handleSubmitSection(key)}
                          disabled={isAnySubmitting}
                          className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-emerald-600 text-white text-xs font-medium hover:bg-emerald-700 disabled:opacity-50"
                        >
                          {isSectionSubmitting ? <Spinner size="sm" tone="current" /> : <Send className="w-3 h-3" />}
                          {isSectionSubmitting ? 'Submitting...' : 'Submit'}
                        </button>
                      )}
                      {isSectionSubmitted && (
                        <span className="flex items-center gap-1 text-xs text-emerald-600 font-medium">
                          <CheckCircle className="w-3 h-3" /> Submitted
                        </span>
                      )}
                      {!isAnySubmitting && (
                        <Link
                          to={section.to}
                          className="text-xs text-purple-600 hover:text-purple-700 dark:text-purple-400 font-medium flex items-center gap-1"
                        >
                          Go to {section.title} <ArrowRight className="w-3 h-3" />
                        </Link>
                      )}
                    </div>
                  </div>
                  <p className="text-xs text-slate-600 dark:text-slate-400 mb-2">{rec.reason}</p>

                  {key === 'guardrails' && analysis.recommendations.guardrails && (
                    <div className="space-y-2">
                      {analysis.recommendations.guardrails.entries.map((e, i) => (
                        <EditableGuardrailEntry
                          key={i}
                          entry={e}
                          editing={isEditing}
                          onUpdate={(field, value) => updateGuardrailEntry(i, field, value)}
                          compact={!isEditing}
                        />
                      ))}
                    </div>
                  )}

                  {key === 'techStack' && analysis.recommendations.techStack && (
                    <div className="space-y-2">
                      {analysis.recommendations.techStack.entries.map((e, i) => (
                        <div key={i}>
                          {isEditing ? (
                            <div className="flex items-center gap-2 p-2 rounded border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
                              <input
                                value={e.name}
                                onChange={(ev) => updateTechStackEntry(i, 'name', ev.target.value)}
                                className="flex-1 text-xs px-2 py-1.5 rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100"
                                placeholder="Name"
                              />
                              <input
                                value={e.version || ''}
                                onChange={(ev) => updateTechStackEntry(i, 'version', ev.target.value)}
                                className="w-20 text-xs px-2 py-1.5 rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100"
                                placeholder="Version"
                              />
                              <select
                                value={e.category}
                                onChange={(ev) => updateTechStackEntry(i, 'category', ev.target.value)}
                                className="text-xs px-2 py-1.5 rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100"
                              >
                                <option value="language">Language</option>
                                <option value="framework">Framework</option>
                                <option value="infrastructure">Infrastructure</option>
                                <option value="database">Database</option>
                                <option value="testing">Testing</option>
                                <option value="cicd">CI/CD</option>
                                <option value="other">Other</option>
                              </select>
                              <input
                                value={e.notes || ''}
                                onChange={(ev) => updateTechStackEntry(i, 'notes', ev.target.value)}
                                className="flex-1 text-xs px-2 py-1.5 rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100"
                                placeholder="Notes"
                              />
                            </div>
                          ) : (
                            <div className="text-xs text-slate-700 dark:text-slate-300 flex items-center gap-2">
                              <Cpu className="w-3 h-3 text-blue-500 shrink-0" />
                              <span className="font-medium">{e.name}</span>
                              {e.version && <span className="text-slate-400">v{e.version}</span>}
                              <span className="text-slate-400">({e.category})</span>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {key === 'claudeMd' && analysis.recommendations.claudeMd && (
                    <div className="space-y-2">
                      {analysis.recommendations.claudeMd.suggestions.map((s, i) => (
                        <div key={i}>
                          {isEditing ? (
                            <div className="flex items-start gap-2">
                              <FileCode className="w-3 h-3 text-purple-500 shrink-0 mt-2" />
                              <input
                                value={s}
                                onChange={(ev) => updateClaudeMdSuggestion(i, ev.target.value)}
                                className="flex-1 text-xs px-2 py-1.5 rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100"
                              />
                            </div>
                          ) : (
                            <div className="text-xs text-slate-700 dark:text-slate-300 flex items-center gap-2">
                              <FileCode className="w-3 h-3 text-purple-500 shrink-0" />
                              {s}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Gap analysis — the former /gaps page, now a second way to feed the
          standards: coverage gaps in the captured requirements. */}
      <GapAnalysisSection />
    </div>
  );
}
